'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Button,
  LoginForm,
  useAuthenticationError,
  type FeedbackMessageBannerProps,
  type LoginFormValues,
} from '@clara/ui';

import { isOk } from '@clara/shared';
import { setUserContext } from '../../../lib/api/user-context/userContextApi';
import { setSentryUser } from '../../../utils/sentry/setSentryUser';
import { authClient } from '../../../../lib/auth/auth-client';

const ERROR_MESSAGES = {
  incorrectCredentials: 'Incorrect email or password.',
  processingError: 'An error occurred during the processing.',
} as const;

export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { message, status, clearMessage } = useAuthenticationError();
  const urlError = searchParams.get('error')?.trim() ?? '';

  const [isLoading, setIsLoading] = useState(false);
  const [banner, setBanner] = useState<Pick<FeedbackMessageBannerProps, 'variant' | 'message'>>({
    variant: urlError ? 'error' : 'info',
    message: urlError ? 'Invalid token. Login failed.' : 'Please enter your email address and password to sign in.',
  });

  useEffect(() => {
    if (message && status) {
      setBanner({ variant: status, message });
      clearMessage();
    }
  }, [message, status, clearMessage]);

  const secondaryBanner = useMemo<Pick<FeedbackMessageBannerProps, 'variant' | 'message'> | null>(() => {
    if (banner.variant !== 'success') return null;
    return { variant: 'info', message: 'Use your new password to sign in.' };
  }, [banner.variant]);

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    try {
      const result = await authClient.signIn.email({
        email: data.email.trim(),
        password: data.password,
      });

      if (result.error) {
        setBanner({
          variant: 'error',
          message: 'message' in result.error ? String(result.error.message) : ERROR_MESSAGES.incorrectCredentials,
        });
        return;
      }

      if ((result.data as { twoFactorRedirect?: boolean })?.twoFactorRedirect) {
        const otpResult = await authClient.twoFactor.sendOtp();
        if (otpResult.error) {
          setBanner({
            variant: 'error',
            message: 'message' in otpResult.error ? String(otpResult.error.message) : ERROR_MESSAGES.processingError,
          });
          return;
        }

        router.push('/mfa');
        return;
      }

      const contextResult = await setUserContext();
      if (isOk(contextResult)) {
        await setSentryUser();
        router.push('/');
      } else if (contextResult.status === 409) {
        router.push('/select-exhibitor');
      } else {
        setBanner({ variant: 'error', message: 'Failed to set user context. Please try again.' });
        return;
      }
    } catch {
      setBanner({ variant: 'error', message: ERROR_MESSAGES.processingError });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginForm onSubmit={onSubmit} isLoading={isLoading} banner={banner} secondaryBanner={secondaryBanner}>
      <Link href="/request-magic-link" className="contents" aria-label="Request a magic link">
        <Button styleType="outline" aria-label="Request a magic link">
          <i className="clara-magic_link-outline" aria-hidden="true" />
          Send Magic Link
        </Button>
      </Link>
    </LoginForm>
  );
}
